import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { message } from "antd";
import DescriptionIcon from '@mui/icons-material/Description';
import CollectionsIcon from '@mui/icons-material/Collections';
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import ForumIcon from '@mui/icons-material/Forum';
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useNavigate, useLocation } from "react-router-dom";
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import Spinner from "../../../components/common/Spinner";
import SakshamSSOButton from "../../../components/common/SakshamSSOButton";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { usePermission } from "../../../permissions/usePermission";

const RESOURCE_CARD_META = [
  {
    title: "How To Implement",
    key: "howToImplement",
    subtitles: ["SOPs and step-by-step implementation guides", "Guidelines for smooth program rollout"],
  },
  {
    title: "Promotional Material",
    key: "promotionalMaterial",
    subtitles: ["Posters, banners, and social media creatives", "Promotional videos, brochures, and PPTs"],
  },
  {
    title: "Program Reporting",
    key: "programReporting",
    subtitles: ["Reporting formats, templates, and links", "Program review and monitoring resources"],
  },
];

const highlightCards = [
  {
    title: "Feedback",
    icon: ForumIcon,
  },
  {
    title: "Testimonials",
    icon: RecordVoiceOverIcon,
  },
  {
    title: "Gallery",
    icon: CollectionsIcon,
  },
];

const fallbackDescription = "Loading..."
const basketColorMap = {
  foundation: {
    basket: "#7BE97B",
    program: "#C8FFC8",
  },
  inception: {
    basket: "#FFD93B",
    program: "#FFEDA3",
  },
  infusion: {
    basket: "#FF9355",
    program: "#FFC5A4",
  },
  elevation: {
    basket: "#64B2FF",
    program: "#CAE4FF",
  },
  national: {
    basket: "#CA8DFF",
    program: "#E7CDFE",
  },
};



const formatProgramDayMonth = (programDayMonth) => {
  const parsed = parseProgramDayMonth(programDayMonth);
  if (!parsed) return "";

  const getOrdinal = (day) => {
    if (day > 3 && day < 21) return "th";

    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return <>
    {parsed.day}
    <sup>{getOrdinal(parsed.day)}</sup>
    {" "}
    {monthNames[parsed.month]}
  </>
};


const getSelectionColors = (basketName = "") => {
  const normalizedName = basketName.trim().toLowerCase();

  const matchedEntry = Object.entries(basketColorMap).find(([key]) =>
    normalizedName.includes(key)
  );

  return (
    matchedEntry?.[1] || {
      basket: "#CBFFCF",
      program: "#EFFFFE",
    }
  );
};

const monthMap = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

const parseProgramDayMonth = (programDayMonth) => {
  if (!programDayMonth || typeof programDayMonth !== "string") return null;
  const cleaned = programDayMonth.replace(",", " ").trim();

  // Supports numeric formats like DD-MM, DD/MM, DD.MM
  const numericMatch = cleaned.match(/^(\d{1,2})[-/.](\d{1,2})$/);
  if (numericMatch) {
    const day = Number.parseInt(numericMatch[1], 10);
    const month = Number.parseInt(numericMatch[2], 10) - 1;
    if (
      Number.isNaN(day) ||
      Number.isNaN(month) ||
      day < 1 ||
      day > 31 ||
      month < 0 ||
      month > 11
    ) {
      return null;
    }
    return { day, month };
  }

  const parts = cleaned.split(/\s+/);
  if (parts.length < 2) return null;

  let day = Number.parseInt(parts[0], 10);
  let monthText = parts[1]?.toLowerCase();

  if (Number.isNaN(day)) {
    day = Number.parseInt(parts[1], 10);
    monthText = parts[0]?.toLowerCase();
  }

  const month = monthMap[monthText];
  if (Number.isNaN(day) || month === undefined) return null;

  return { day, month };
};

const getDefaultProgramForBasket = (basket) => {
  const programs = basket?.programs || [];
  if (!programs.length) return null;

  const isFoundationBasket = (basket?.name || "")
    .toLowerCase()
    .includes("foundation");
  if (!isFoundationBasket) return programs[0];

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const normalizedPrograms = programs.map((program, index) => {
    const parsed = parseProgramDayMonth(program?.programDayMonth);
    if (!parsed) return { program, index, date: null };

    let nextOccurrence = new Date(today.getFullYear(), parsed.month, parsed.day);
    if (nextOccurrence < today) {
      nextOccurrence = new Date(today.getFullYear() + 1, parsed.month, parsed.day);
    }
    return { program, index, date: nextOccurrence };
  });

  const upcomingPrograms = normalizedPrograms
    .filter((item) => item.date)
    .sort((a, b) => a.date - b.date || a.index - b.index);

  return upcomingPrograms[0]?.program || programs[0];
};

const ProgramsExplorer = () => {
  const S3_BASE_URL = "https://vmsprojectdata.s3.ap-south-1.amazonaws.com/";
  const STORAGE_KEY = "nonVerifiedProgramsExplorerSelection";
  const [baskets, setBaskets] = useState([]);
  const [selectedBasket, setSelectedBasket] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [programDetails, setProgramDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const { canByKey } = usePermission();
  const canUpdateProgram = canByKey("PROGRAM_MANAGEMENT_PROGRAMS_UPDATE");
  const canViewProgramContent = canByKey(
    "PROGRAM_MANAGEMENT_PROGRAM_CONTENT_VIEW",
  );

  const location = useLocation();
  const selectedBasketId = location.state?.selectedBasketId;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const persistSelection = (basketId, programId) => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ basketId: basketId || null, programId: programId || null })
      );
    } catch (error) {
      // Ignore storage write errors to avoid blocking the UI flow.
    }
  };

  const readPersistedSelection = () => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  };

  const clearPersistedSelection = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Ignore storage clear errors.
    }
  };

  useEffect(() => {
    const fetchBaskets = async () => {
      try {
        setLoading(true);

        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/programBaskets`
        );
        const data = res.data.data || [];
        // console.log("Fetched Baskets:", data);
        // setBaskets(data);

        // if (data.length > 0) {
        //   const firstBasket = data[0];
        //   setSelectedBasket(firstBasket);
        //   setSelectedProgram(firstBasket.programs?.[0] || null);


        const filteredData = data.map(basket => ({
          ...basket,
          programs: basket.programs?.filter(
            program => program.name !== "Foundation Programs" // adjust condition if needed
          )
        }));
        setBaskets(filteredData);


        if (filteredData.length > 0) {
          const savedSelection = readPersistedSelection();
          const navigatedBasket = selectedBasketId
            ? filteredData.find(
              (basket) => basket._id === selectedBasketId
            )
            : null;

          const savedBasket = savedSelection?.basketId
            ? filteredData.find((basket) => basket?._id === savedSelection.basketId)
            : null;

          const activeBasket = navigatedBasket || savedBasket || filteredData[0];

          const savedProgram = savedSelection?.programId
            ? activeBasket?.programs?.find(
              (program) => program?._id === savedSelection.programId
            )
            : null;
          const activeProgram = savedProgram || getDefaultProgramForBasket(activeBasket);

          setSelectedBasket(activeBasket);
          setSelectedProgram(activeProgram);
          clearPersistedSelection();
        }

      } catch (err) {
        console.error(err);
        setLoading(false);

      }
      setLoading(false);

    };

    fetchBaskets();
  }, []);

  useEffect(() => {
    if (!selectedProgram?._id) return;

    const fetchProgramDetails = async () => {
      try {
        setLoading(true);

        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/programs/${selectedProgram._id}`
        );
        setProgramDetails(res.data);
      } catch (err) {
        setLoading(false);

        console.error(err);
      }
      setLoading(false);

    };

    fetchProgramDetails();
  }, [selectedProgram]);

  const handleBasketSelect = (basket) => {
    const defaultProgram = getDefaultProgramForBasket(basket);
    setSelectedBasket(basket);
    setSelectedProgram(defaultProgram);
  };

  const scrollPrograms = (direction) => {
    if (!scrollRef.current) return;

    scrollRef.current.scrollBy({
      left: direction === "left" ? -190 : 190,
      behavior: "smooth",
    });
  };

  const programData = programDetails?.program || programDetails || {};
  const currentProgramId = selectedProgram?._id || programData?._id || "";
  const selectedProgramName = programData?.name || selectedProgram?.name || "Loading...";
  const selectedProgramDayMonth =
    programData?.programDayMonth || selectedProgram?.programDayMonth || "";
  const selectedProgramDescription =
    programData?.description || fallbackDescription;
  const selectedProgramDescriptionHtml = DOMPurify.sanitize(
    selectedProgramDescription,
  );
  const resolveAssetUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:")) {
      return url;
    }
    const baseUrl = process.env.REACT_APP_BASE_URL || "";
    const baseNoApi = baseUrl.replace(/\/?api\/?$/, "");
    const normalized = url.startsWith("/") ? url.slice(1) : url;
    if (normalized.startsWith("profile-images/")) {
      return `${S3_BASE_URL}${normalized}`;
    }
    return url.startsWith("/") ? `${baseNoApi}${url}` : `${baseNoApi}/${url}`;
  };

  const selectedProgramImage = resolveAssetUrl(
    programData?.programPicture
  );
  const resourceCards = RESOURCE_CARD_META.map((card) => {
    const items = Array.isArray(programData?.[card.key]) ? programData[card.key] : [];
    return {
      ...card,
      items,
    };
  });

  const basketItems = useMemo(() => baskets || [], [baskets]);
  // const programItems = selectedBasket?.programs || [];

  const programItems = useMemo(() => {
    if (!selectedBasket?.programs) return [];

    return selectedBasket.programs
      .map((program) => ({
        ...program,
        date: parseProgramDayMonth(program?.programDayMonth),
      }))
      .sort((a, b) => {
        if (!a.date) return 1;
        if (!b.date) return -1;
        if (a.date.month !== b.date.month) return a.date.month - b.date.month;
        return a.date.day - b.date.day;
      });
  }, [selectedBasket]);

  const isSmartGirlProgram =
    selectedProgramName.trim().toLowerCase() === "smart girl";
  const isSakshamProgram =
    selectedProgramName.trim().toLowerCase().includes("saksham");
  const getResourceCardTitle = (card) =>
    card?.key === "programReporting" && isSmartGirlProgram
      ? "Program Schedule"
      : card?.title;
  const getResourceCardSubtitles = (card) =>
    card?.key === "programReporting" && isSmartGirlProgram
      ? ["Workshops, Events and Webinars"]
      : card?.subtitles || [];

  const handleResourceCardClick = (card) => {
    // if (!card || !canViewProgramContent) return;
    if (!card) return;
    if (card.key === "howToImplement") {
      if (!card.items.length) {
        message.info("No implementation resources available for this program.");
        return;
      }

      persistSelection(selectedBasket?._id, selectedProgram?._id || programData?._id);
      const howToProgramId = selectedProgram?._id || programData?._id || "";
      navigate(`/HowToImplement${howToProgramId ? `?programId=${howToProgramId}` : ""}`, {
        state: {
          programId: selectedProgram?._id || programData?._id || null,
          programName: selectedProgramName,
          resources: card.items,
        },
      });
      return;
    }

    if (card.key === "promotionalMaterial") {
      if (!selectedProgram?._id) {
        message.info("No program selected.");
        return;
      }

      if (!card.items.length) {
        message.info("No promotional material available for this program.");
        return;
      }

      persistSelection(selectedBasket?._id, selectedProgram?._id || programData?._id);
      navigate(`/programs/${selectedProgram._id}/media`, {
        state: {
          programName: selectedProgramName || selectedProgram?.name,
          resources: card.items,
        },
      });
      return;
    }

    if (card.key === "programReporting" && isSmartGirlProgram) {
      persistSelection(selectedBasket?._id, selectedProgram?._id || programData?._id);
      navigate("/ProgramScheduledEvents");
      return;
    }

    if (card.key === "programReporting" && !isSmartGirlProgram) {
      if (!card.items.length) {
        message.info("No program reporting resources available for this program.");
        return;
      }

      persistSelection(selectedBasket?._id, selectedProgram?._id || programData?._id);

      // navigate("/programs/reporting", {
      //   state: {
      //     programId: selectedProgram?._id || programData?._id || null,
      //     programName: selectedProgramName,
      //     resources: card.items,
      //   },
      // });

      const resolvedProgramId =
        selectedProgram?._id || programData?._id;

      navigate(`/programs/reporting/${resolvedProgramId}`, {
        state: {
          programName: selectedProgramName,
          resources: card.items,
        },
      });

    }
  };

  const selectedColors = getSelectionColors(selectedBasket?.name);

  return (
    <div
      style={{
        background: "#ffffff",
        minHeight: "100vh",
        padding: "40px 0 60px",
      }}
    >
      <style>
        {`
          .programs-explorer-shell {
            max-width: 1300px;
            margin: 0 auto;
            padding: 0 36px;
            color: #2b2b2b;
            font-family: "Helvetica Neue", Arial, sans-serif;
          }

          .programs-basket-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
            gap: 14px;
            margin-bottom: 20px;
          }

          .programs-nav-row {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 20px;
          }

          .programs-scroll-list {
            display: flex;
            align-items: center;
            gap: 24px;
            overflow-x: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;
            flex: 1;
          }

          .programs-scroll-list::-webkit-scrollbar {
            display: none;
          }

          .programs-title-block {
            text-align: center;
            margin-bottom: 20px;
          }

          .programs-title {
            margin: 0 0 8px;
            font-size: 24px;
            font-weight: 700;
            color: #262626;
          }

          .programs-date {
            font-size: 18px;
            font-weight: 600;
          }

          .programs-main-grid {
            display: grid;
            grid-template-columns: minmax(0, 1.35fr) minmax(320px, 1fr);
            gap: 14px;
            align-items: start;
          }

          .programs-resource-grid {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 14px;
            margin-top: 14px;
          }

          .programs-image-panel {
            background: #fff4f1;
            border-radius: 8px;
            padding: 22px 20px;
            min-height: 100%;
            // min-height: 270px;
            display: flex;
            align-items: center;
            justify-content: center;
            align-self: start;
          }

          .program-details-card {
            max-height: 250px;
            overflow: hidden;
          }

          .programs-image {
            width: 100%;
            max-width: 425px;
            max-height: 325px;
            object-fit: contain;
          }

          .program-description-content {
            font-size: 13px;
            line-height: 1.6;
            color: #535353;
            max-height: 164px;
            overflow-y: auto;
            padding-right: 8px;
            word-break: break-word;
          }

          .program-description-content::-webkit-scrollbar {
            width: 6px;
          }

          .program-description-content::-webkit-scrollbar-track {
            background: #ffe8e2;
            border-radius: 999px;
          }

          .program-description-content::-webkit-scrollbar-thumb {
            background: #efb6a8;
            border-radius: 999px;
          }

          .program-description-content p {
            margin: 0 0 8px;
          }

          .program-description-content p:last-child {
            margin-bottom: 0;
          }

          .program-description-content h1,
          .program-description-content h2,
          .program-description-content h3 {
            margin: 0 0 10px;
            color: #343434;
            line-height: 1.3;
          }

          .program-description-content h1 {
            font-size: 22px;
          }

          .program-description-content h2 {
            font-size: 19px;
          }

          .program-description-content h3 {
            font-size: 16px;
          }

          .program-description-content ul,
          .program-description-content ol {
            margin: 0 0 8px;
            padding-left: 1.35rem;
          }

          .program-description-content blockquote {
            border-left: 3px solid #efb6a8;
            color: #5d5d5d;
            margin: 0 0 10px;
            padding-left: 12px;
          }

          .program-description-content code {
            background: #fff;
            border-radius: 4px;
            color: #5c2d7d;
            font-size: 0.92em;
            padding: 2px 4px;
          }

          .program-description-content pre {
            background: #343434;
            border-radius: 6px;
            color: #fff;
            margin: 0 0 10px;
            overflow-x: auto;
            padding: 12px;
          }

          .program-description-content pre code {
            background: transparent;
            color: inherit;
            padding: 0;
          }

          .program-description-content hr {
            border: 0;
            border-top: 1px solid #efc8bd;
            margin: 14px 0;
          }

          .programs-highlights-header {
            display: flex;
            align-items: center;
            gap: 16px;
            margin-top: 44px;
          }

          .programs-highlights-grid {
            display: flex;
            justify-content: center;
            gap: 18px;
            flex-wrap: wrap;
            margin-top: 28px;
          }

          @media (max-width: 900px) {
            .programs-explorer-shell {
              padding: 0 24px;
            }

            .programs-main-grid {
              grid-template-columns: 1fr;
            }

            .programs-image-panel {
              min-height: 220px;
            }

            .programs-resource-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }

          @media (max-width: 640px) {
            .programs-explorer-shell {
              padding: 0 16px;
            }

            .programs-basket-grid {
              grid-template-columns: repeat(3, minmax(0, 1fr));
              display: flex;
              flex-wrap: wrap;
              justify-content: center;
              gap: 10px;
            }

            .programs-basket-grid button {
              flex: 0 0 calc(33.33% - 10px);
              max-width: 250px;
            }

            .programs-nav-row {
              gap: 8px;
              margin-bottom: 16px;
            }

            .program-scroll-arrow {
              display: none !important;
            }

            .programs-scroll-list {
              gap: 10px;
              padding-bottom: 4px;
            }

            .programs-title {
              font-size: 20px;
              line-height: 1.35;
            }

            .programs-date {
              font-size: 15px;
            }

            .programs-resource-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .programs-image-panel {
              padding: 18px 16px;
              min-height: 180px;
            }

            .programs-highlights-header {
              gap: 10px;
              margin-top: 32px;
            }

            .programs-highlights-grid {
              gap: 12px;
            }
          }
        `}
      </style>
      <Spinner loading={loading} />
      <div
        className="programs-explorer-shell"
        style={{}}
      >
        <div
          className="programs-basket-grid"
          style={{}}
        >
          {basketItems.map((basket) => {
            const words = (basket.name || "").split(" ");
            const topLine = words.slice(0, -1).join(" ") || basket.name;
            const bottomLine = words.length > 1 ? words.slice(-1).join(" ") : "";
            const isActive = selectedBasket?._id === basket._id;

            return (
              <button
                key={basket._id}
                onClick={() => handleBasketSelect(basket)}
                style={{
                  minHeight: "58px",
                  borderRadius: "6px",
                  border: isActive
                    ? `1px solid ${getSelectionColors(basket.name).basket}`
                    : "1px solid #cdcdcd",
                  background: isActive
                    ? getSelectionColors(basket.name).program
                    : "#E6E6E6",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#303030",
                  lineHeight: 1.25,
                  cursor: "pointer",
                  padding: "12px 10px",
                }}
              >
                <div>{topLine}</div>
                {bottomLine ? <div>{bottomLine}</div> : null}
              </button>
            );
          })}
        </div>


        <div style={{ textAlign: "center", marginTop: "20px", marginBottom: "20px" }}>
          <h4 style={{ marginBottom: "5px", fontSize: "18px" }}><b>{selectedBasket?.name}</b></h4>
          <p>{selectedBasket?.description}</p>
        </div>

        <div
          className="programs-nav-row"
          style={{}}
        >
          <button
            onClick={() => scrollPrograms("left")}
            aria-label="Scroll programs left"
            className="program-scroll-arrow"
            style={{
              //    width: "26px",
              //  height: "26px",
              borderRadius: "50%",
              border: "1px solid #d7d7d7",
              background: "#fff",
              color: "#9d9d9d",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              paddingTop: "8px",
              paddingBottom: "8px",

            }}
          >
            <KeyboardArrowLeftRoundedIcon sx={{ fontSize: 24 }} />
          </button>

          <div
            ref={scrollRef}
            className="programs-scroll-list"
            style={{}}
          >
            {programItems.map((program) => {
              const isActive = selectedProgram?._id === program._id;

              return (
                <button
                  key={program._id}
                  onClick={() => {
                    setSelectedProgram(program);
                  }}
                  style={{
                    whiteSpace: "nowrap",
                    borderRadius: "7px",
                    border: isActive
                      ? `1px solid ${selectedColors.basket}`
                      : "1px solid transparent",
                    background: isActive ? selectedColors.program : "transparent",
                    color: isActive ? "#303030" : "#7c7c7c",
                    fontSize: "15px",
                    fontWeight: isActive ? 600 : 500,
                    padding: "8px 12px",
                    cursor: "pointer",
                  }}
                >
                  {program.name}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => scrollPrograms("right")}
            aria-label="Scroll programs right"
            className="program-scroll-arrow"
            style={{
              //  width: "26px",
              // height: "26px",
              borderRadius: "50%",
              border: "1px solid #d7d7d7",
              background: "#fff",
              color: "#9d9d9d",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              paddingTop: "8px",
              paddingBottom: "8px",
            }}
          >
            <KeyboardArrowRightRoundedIcon sx={{ fontSize: 24 }} />
          </button>
        </div>

        <div style={{ borderTop: "1px solid #ebe1dc", marginBottom: "18px" }} />

        <div className="programs-title-block" style={{}}>
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <div style={{ width: 120 }} />

            <div style={{ flex: 1, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <h2 className="programs-title" style={{ margin: "0 0 6px 0" }}>
                {selectedProgramName}
              </h2>

              {selectedProgramDayMonth && (
                <span className="programs-date" style={{ marginBottom: "10px" }}>
                  {formatProgramDayMonth(selectedProgramDayMonth)}
                </span>
              )}

              {/* {isSakshamProgram && (
                <div style={{ marginTop: "10px" }}>
                  <SakshamSSOButton variant="button" />
                </div>
              )} */}
            </div>

            {canUpdateProgram && (
              <button
                type="button"
                onClick={() => currentProgramId && navigate(`/editProgram/${currentProgramId}`)}
                disabled={!currentProgramId}
                style={{
                  width: "fit-content",
                  border: "1px solid #d7c9f2",
                  background: "#ffffff",
                  color: "#6f3dbd",
                  borderRadius: "7px",
                  fontSize: "12px",
                  fontWeight: 600,
                  padding: "5px 10px",
                  cursor: currentProgramId ? "pointer" : "not-allowed",
                  opacity: currentProgramId ? 1 : 0.6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 14 }} />
                {!isMobile && "Edit"}
              </button>
            )}
          </div>
        </div>

        <div
          className="programs-main-grid"
          style={{}}
        >
          <div>
            <div
              className="program-details-card"
              style={{
                background: "#fff4f1",
                border: "1px solid #f1d4cb",
                borderRadius: "8px",
                padding: "24px 24px 20px",
                minHeight: "210px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "12px",
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#343434",
                }}
              >
                <DescriptionIcon sx={{ color: "#ee8f7d", fontSize: 20 }} />
                <span>Program Details</span>
              </div>
              <div
                className="program-description-content"
                dangerouslySetInnerHTML={{
                  __html: selectedProgramDescriptionHtml,
                }}
              />
            </div>

            <div
              className="programs-resource-grid"
              style={{}}
            >
              {resourceCards.map((card) => {
                const cardTitle = getResourceCardTitle(card);
                const cardSubtitles = getResourceCardSubtitles(card);
                const isCardClickable =
                  // canViewProgramContent &&
                  ((card.key === "howToImplement" && card.items.length > 0) ||
                    (card.key === "promotionalMaterial" &&
                      Boolean(selectedProgram?._id) &&
                      card.items.length > 0) ||
                    (card.key === "programReporting" &&
                      (isSmartGirlProgram || card.items.length > 0)));

                return (
                  <div
                    key={card.key}
                    style={{
                      background: "#fff4f1",
                      border: "1px solid #f1d4cb",
                      borderRadius: "8px",
                      minHeight: "96px",
                      padding: "16px 14px 14px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: 700,
                          color: "#343434",
                          marginBottom: "8px",
                        }}
                      >
                        {cardTitle}
                      </div>
                      <ul style={{ fontSize: "12px", lineHeight: 1.45, color: "#5d5d5d", maxWidth: "92%", margin: 0, paddingLeft: 0, listStyle: "none" }}>
                        {cardSubtitles.map((subtitle) =>
                        (
                          <li key={subtitle} style={{ display: "flex", gap: "6px" }}>
                            <span aria-hidden="true" style={{ flex: "0 0 auto" }}>•</span>
                            <span>{subtitle}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "10px",
                      }}
                    >
                      <span
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "50%",
                          background: "#ffd9d2",
                          color: "#ee8f7d",
                          display: "grid",
                          placeItems: "center",
                          cursor: isCardClickable ? "pointer" : "default",
                          opacity: isCardClickable ? 1 : 0.5,
                        }}
                        onClick={() => isCardClickable && handleResourceCardClick(card)}
                      >
                        <ArrowForwardIosRoundedIcon sx={{ fontSize: 21 }} />
                      </span>
                    </div>
                  </div>
                );
              })}
              {isMobile && <div
                className="programs-image-panel"
              >
                {selectedProgramImage ? (
                  <img
                    src={selectedProgramImage}
                    alt={selectedProgramName}
                    className="programs-image"
                    style={{}}
                  />
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      fontSize: "28px",
                      fontWeight: 600,
                      letterSpacing: "0.03em",
                      color: "#252525",
                    }}
                  >
                    {selectedProgramName.toUpperCase()}
                  </div>
                )}
              </div>}
            </div>
          </div>

          {!isMobile && <div
            className="programs-image-panel"
            style={{}}
          >
            {selectedProgramImage ? (
              <img
                src={selectedProgramImage}
                alt={selectedProgramName}
                className="programs-image"
                style={{}}
              />
            ) : (
              <div
                style={{
                  textAlign: "center",
                  fontSize: "40px",
                  fontWeight: 800,
                  letterSpacing: "0.03em",
                  color: "#252525",
                }}
              >
                {selectedProgramName.toUpperCase()}
              </div>
            )}
          </div>}
        </div>

        {/* <div
          className="programs-highlights-header"
          style={{}}
        >
          <div style={{ flex: 1, borderTop: "1px solid #efc8bd" }} />
          <div
            style={{
              fontSize: "17px",
              fontWeight: 700,
              color: "#2f2f2f",
            }}
          >
            Highlights
          </div>
          <div style={{ flex: 1, borderTop: "1px solid #efc8bd" }} />
        </div>

        <div
          className="programs-highlights-grid"
          style={{}}
        >
          {highlightCards.map((card) => {
            const Icon = card.icon;

            return (
              <div
                key={card.title}
                style={{
                  width: "148px",
                  height: "148px",
                  border: "1px solid #efb6a8",
                  borderRadius: "8px",
                  background: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  cursor: "pointer",
                }}
                onClick={card.title === "Gallery" ? () => navigate("/Gallery") : card.title === "Feedback" ? () => navigate("/Feedback") : card.title === "Testimonials" ? () => navigate("/Testimonials") : null}
              >
                <Icon sx={{ color: "#E98676", fontSize: 46 }} />
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#353535",
                  }}
                >
                  {card.title}
                </div>
              </div>
            );
          })}
        </div> */}
      </div>
    </div>
  );
};

export default ProgramsExplorer;



